// useCollectionsNotifications — global subscription that feeds the TopBar
// notification bell.
//
// Mounted once (in AppShell). On mount it seeds the counts from the REST
// endpoint, then opens a Server-Sent Events stream for forward-going deltas.
// Because the browser's native EventSource can't set an Authorization header,
// the access token rides along as a `?token=` query param (the server
// promotes it to a Bearer header — see collections/notifications/controller).
//
// Gated to back-office staff roles. weighbridge_operator (kiosk-only) and the
// driver realm don't see the Collections surface, so they don't subscribe.
//
// SSE only carries deltas from the moment of connection, so we re-seed from
// REST on every (re)connect to heal any frame missed while disconnected.

import { useCallback, useEffect, useRef } from 'react';

import { apiClient } from '../api/client';
import { endpoints } from '../api/endpoints';
import { useAuthStore } from '../store/authStore';
import {
  useCollectionsNotifStore,
  type CollectionsCounts,
} from '../store/collectionsNotifStore';
import { sseBaseUrl, useSseWithTokenRefresh } from './useSseWithTokenRefresh';

const SUBSCRIBED_ROLES = new Set(['staff', 'manager', 'admin']);

function parseCounts(raw: unknown): CollectionsCounts | null {
  if (typeof raw !== 'string') return null;
  let data: Record<string, unknown>;
  try {
    data = JSON.parse(raw) as Record<string, unknown>;
  } catch {
    return null;
  }
  const pending = data['pendingReceipts'];
  const approvedUnsent = data['approvedUnsentReceipts'];
  const cancellations = data['cancellationRequests'];
  if (
    typeof pending !== 'number' ||
    typeof approvedUnsent !== 'number' ||
    typeof cancellations !== 'number'
  ) {
    return null;
  }
  // Older servers do not send this field yet: treat a missing value as 0.
  const conflicts = data['completedAfterCancellation'];
  return {
    pendingReceipts: pending,
    approvedUnsentReceipts: approvedUnsent,
    cancellationRequests: cancellations,
    completedAfterCancellation: typeof conflicts === 'number' ? conflicts : 0,
  };
}

export function useCollectionsNotifications(): void {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const role = useAuthStore((s) => s.user?.role);
  const setCounts = useCollectionsNotifStore((s) => s.setCounts);
  const reset = useCollectionsNotifStore((s) => s.reset);
  const setActive = useCollectionsNotifStore((s) => s.setActive);

  const enabled = isAuthenticated && role !== undefined && SUBSCRIBED_ROLES.has(role);

  // Keep the latest setter in a ref so its identity doesn't re-run the effect.
  const setCountsRef = useRef(setCounts);
  setCountsRef.current = setCounts;

  // REST seed / resync. The cancellation guard lives independent of the SSE
  // connection lifecycle (the helper now owns reconnection) — it only trips
  // on unmount / disable so a late seed response can't write into a torn-down
  // subscription. `seed` is stable (reads refs) so it doesn't re-run effects.
  const cancelledRef = useRef(false);
  const seed = useCallback((): void => {
    apiClient
      .get<CollectionsCounts>(endpoints.collectionsNotifications.counts)
      .then((res) => {
        if (!cancelledRef.current) setCountsRef.current(res.data);
      })
      .catch(() => {
        // Non-fatal — the SSE stream will deliver the next update.
      });
  }, []);

  // Eager seed + reset wiring (separate from the SSE connection): seed once on
  // enable, reset the store on disable. setActive mirrors the enabled state so
  // TopBar only shows the bell while CollectionsLayout is mounted.
  useEffect(() => {
    if (!enabled) {
      reset();
      setActive(false);
      return;
    }
    setActive(true);
    cancelledRef.current = false;
    seed();
    return () => {
      cancelledRef.current = true;
      setActive(false);
    };
  }, [enabled, reset, seed, setActive]);

  // The live stream — the shared helper re-reads a fresh token on every
  // reconnect, and we re-seed counts on each server `connected` frame to heal
  // any delta missed while disconnected.
  useSseWithTokenRefresh(
    (token) =>
      `${sseBaseUrl()}${endpoints.collectionsNotifications.stream}${
        token ? `?token=${encodeURIComponent(token)}` : ''
      }`,
    {
      connected: () => seed(),
      collections_counts: (ev) => {
        const counts = parseCounts(ev.data);
        if (counts !== null) setCountsRef.current(counts);
      },
    },
    { enabled, realm: 'tenant' },
  );
}
